import React, {useMemo, useState} from "react";
import styled from "styled-components";
import {differenceInDays} from "date-fns";
import {numberWithCommas} from "../commonmethod/CommonMethod";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../shadCn/components/ui/dialog";

/** @deprecated 다른 컴포넌트 호환용 */
export const ModalTable = styled.table`
    table-layout: fixed;
    width: 100%;
    border-collapse: collapse;
    margin-top: 7px;
    margin-bottom: 10px;
`;

/** @deprecated 다른 컴포넌트 호환용 */
export const ModalTh = styled.th`
    border: 1px solid #e5e5e5;
    text-align: ${(props) => props.align || "center"};
    width: 25%;
    background: ${(props) => props.background || "#ffffff"};
    padding: ${(props) => props.padding || "10px 1.5rem"};
`;

/** @deprecated 다른 컴포넌트 호환용 */
export const ModalTd = styled.td`
    border: 1px solid #e5e5e5;
    text-align: left;
    width: 75%;
    padding: ${(props) => props.padding || "10px 1.5rem"};
    overflow: hidden;
    white-space: nowrap;

    & > input {
        width: 100%;
        box-sizing: border-box;
    }
`;

const GaugeContainer = styled.div`
    position: relative;
    display: inline-block;
    align-content: center;

    &:hover > div.tooltip {
        display: block;
    }
`;

const HoverBox = styled.div`
    display: none;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 8px;
    min-width: 220px;
    padding: 12px 14px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.1);
    z-index: 10;
`;

function resolveBarColor(value) {
    if (value <= 40) return "bg-emerald-500";
    if (value <= 60) return "bg-amber-400";
    if (value <= 80) return "bg-orange-500";
    return "bg-red-500";
}

function resolvePlatformTitle(platform) {
    if (platform === "ITM") return "SIMMS-ITM V3";
    if (platform === "IAM") return "SIMMS-IAM V3";
    if (platform === "GOV") return "SIMMS-GOV V3";
    return "SIMMS-SRM V3";
}

function resolveLicenseMetrics(license) {
    const type = license.expire === "2099-12-31" ? "purchase" : "rental";
    const assetPercent =
        license.assetLicenseCount !== 0
            ? Number(((license.assetUsedCount / license.assetLicenseCount) * 100).toFixed(2))
            : 0;
    const userPercent =
        license.userLicenseCount !== 0
            ? Number(((license.userUsedCount / license.userLicenseCount) * 100).toFixed(2))
            : 0;
    const leftDate = differenceInDays(new Date(license.expire), new Date());
    const datePercent =
        type === "purchase"
            ? 0
            : (() => {
                  const now = new Date();
                  const expire = new Date(license.expire);
                  const msPerMonth = 1000 * 60 * 60 * 24 * 30;
                  const diffMs = expire - now;
                  const monthsLeft = diffMs > 0 ? Math.ceil(diffMs / msPerMonth) : 0;

                  if (monthsLeft <= 1) return 90;
                  if (monthsLeft <= 3) return 70;
                  if (monthsLeft <= 6) return 50;
                  if (monthsLeft <= 12) return 10;
                  return 1;
              })();

    return {type, assetPercent, userPercent, leftDate, datePercent};
}

function LicenseProgressBar({license, platform}) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const metrics = useMemo(
        () => (license ? resolveLicenseMetrics(license) : null),
        [license],
    );

    if (!license || !metrics) {
        return null;
    }

    const title = resolvePlatformTitle(platform);
    const contractLabel = metrics.type === "purchase" ? "영구 라이선스" : "임대 라이선스";
    const isSrm = platform === "SRM";
    const usageLabel = isSrm ? "사용자" : "자산";
    const usageUsed = isSrm ? license.userUsedCount : license.assetUsedCount;
    const usageTotal = isSrm ? license.userLicenseCount : license.assetLicenseCount;
    const usagePercent = isSrm ? metrics.userPercent : metrics.assetPercent;
    const unlimited = usageTotal === 0;
    const expireText =
        metrics.datePercent === 0
            ? "무기한"
            : `${license.expire} · D-${metrics.leftDate}`;

    const handleOpenModal = (e) => {
        e.stopPropagation();
        setIsModalOpen(true);
    };

    return (
        <GaugeContainer>
            <button
                type="button"
                className="h-[28px] cursor-pointer overflow-hidden rounded-2xl bg-[#f3f9ff] px-3 text-[12px] leading-[28px] text-[#4393f0] transition-colors hover:bg-[#e8f4ff]"
                onClick={handleOpenModal}
            >
                라이선스
            </button>

            <HoverBox className="tooltip">
                <p className="text-[13px] font-medium text-[#334155]">
                    {title} · {contractLabel}
                </p>
                <p className="mt-1 text-[12px] text-[#64748b]">
                    {usageLabel}{" "}
                    {unlimited
                        ? "무제한"
                        : `${numberWithCommas(usageUsed)} / ${numberWithCommas(usageTotal)} (${usagePercent}%)`}
                </p>
                <p className="mt-1 text-[12px] text-[#64748b]">만료일 {expireText}</p>
            </HoverBox>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="gap-5 sm:max-w-[380px]">
                    <DialogHeader className="space-y-1 text-left">
                        <DialogTitle className="text-[17px] font-semibold text-[#133E87]">
                            라이선스
                        </DialogTitle>
                        <DialogDescription className="text-[13px] text-[#64748b]">
                            {title} · {contractLabel}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-[13px] text-[#64748b]">{usageLabel} 라이선스</span>
                            {!unlimited && (
                                <span className="text-[13px] font-medium text-[#4393f0]">
                                    {usagePercent}%
                                </span>
                            )}
                        </div>
                        <p className="text-[20px] font-semibold leading-none text-[#133E87]">
                            {unlimited
                                ? "무제한"
                                : `${numberWithCommas(usageUsed)} / ${numberWithCommas(usageTotal)}`}
                        </p>
                        {!unlimited && (
                            <div className="h-2.5 overflow-hidden rounded-full bg-[#e2e8f0]">
                                <div
                                    className={`h-full rounded-full ${resolveBarColor(usagePercent)}`}
                                    style={{width: `${Math.min(Math.max(usagePercent, 0), 100)}%`}}
                                />
                            </div>
                        )}
                        <p className="pt-1 text-[13px] text-[#64748b]">만료일 {expireText}</p>
                    </div>
                </DialogContent>
            </Dialog>
        </GaugeContainer>
    );
}

export default LicenseProgressBar;
